import styled, { css } from 'styled-components';
import { IslandProps, StyledIslandButtonProps } from './types';

export const getDimension = <P extends IslandProps>(p: P) =>
  p.size && p.size === 'big' ? '40px' : 'var(--spacing-xl)';

export const getPadding = <P extends IslandProps>(p: P) =>
  p.size && p.size === 'big' ? 'var(--spacing-m)' : 'var(--spacing-s)';

export const getPaddingExtremes = <P extends IslandProps>(p: P) =>
  p.size && p.size === 'big' ? 'var(--spacing-l)' : 'var(--spacing-m)';

const activeCSSVars = css`
  --l-background: var(--color-interface-layout-lighter);
  --l-border-color: var(--color-interface-secondary-lighter);
`;

const inactiveCSSVars = css`
  --l-background: var(--color-interface-neutral-light);
  --l-border-color: var(--color-interface-layout-normal);
`;

const dynamicCSSVars = css`
  --l-color: inherit;
  --l-interaction-border-color: var(--color-interface-secondary-lighter);
`;

const staticCSSVars = css`
  --l-color: var(--color-interface-layout-normal);
  --l-interaction-border-color: var(--color-interface-layout-normal);
`;

const applyScaleTransform =
  (scaleVal: number) =>
  <P extends StyledIslandButtonProps>(p: P) =>
    !p.$active &&
    css`
      transform: scale(${scaleVal});
    `;

export const StyledContainer = styled.div<IslandProps>`
  --button-height: ${getDimension};
  --button-width: ${getDimension};
  --button-padding: ${getPadding};
  --button-padding-extreme: ${getPaddingExtremes};

  display: flex;
  width: max-content;
  color: var(--color-interface-secondary-normal);
  align-items: center;

  > div:first-child > div > button, // Avoid problems with the dropdown
  > button:first-of-type {
    padding-left: var(--button-padding-extreme);
    border-radius: var(--radius-100) 0 0 var(--radius-100);
    border-left: 1px solid var(--l-border-color);
  }

  > div:last-child > div > button, // Avoid problems with the dropdown
  > button:last-of-type {
    padding-right: var(--button-padding-extreme);
    border-radius: 0 var(--radius-100) var(--radius-100) 0;
  }

  > div:only-of-type > div > button, // Avoid problems with the dropdown
  > button:only-of-type {
    border-radius: var(--radius-100);
  }

  ${(p) =>
    p.disabled &&
    css`
      pointer-events: none;
    `}
`;

export const StyledButton = styled.button<StyledIslandButtonProps>`
  ${(p) => (p.$active ? activeCSSVars : inactiveCSSVars)}
  ${(p) => (p.$static ? staticCSSVars : dynamicCSSVars)}

  height: var(--button-height);
  min-width: var(--button-width);
  padding: var(--button-padding);
  position: relative;

  color: var(--l-color);
  background: var(--l-background);
  cursor: pointer;
  border: 1px solid var(--l-border-color);
  border-left: ${(p) =>
    p.$active ? '1px solid var(--l-border-color)' : '1px solid transparent'};
  font: inherit;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  transition: var(--transition-fast);

  > div {
    display: flex;
    justify-content: center;
    align-items: center;
    animation: none;
    transition: var(--transition-fast);
  }

  ${(p) =>
    !p.$disabled &&
    css<StyledIslandButtonProps>`
      :hover {
        white-space: nowrap;
        border: 1px solid var(--l-interaction-border-color) !important;

        ${(p) => !p.$active && 'z-index: 2;'}
        ${applyScaleTransform(1.2)}
              > div {
          ${applyScaleTransform(0.8335)}
        }
      }
    `}

  ${(p) =>
    p.$disabled &&
    css`
      cursor: default;
      color: var(--color-interface-layout-normal);
    `}
`;
